import { useState, useEffect, useRef } from 'react'
import { languageUtil, onLanguageChange } from '../../util/languageUtils'
import { fetchExchangeRate } from '../../util/currency'
import { useCurrency } from '../../util/CurrencyContext'
import { supportedCurrencies } from '../../constants'
import { useTranslation } from 'react-i18next'
import { useCart } from '../../util/CartContext'

const LanguageCurrencyModal = ({ onClose }) => {
    const { language, setLanguage } = languageUtil()
    const { currency, updateCurrency } = useCurrency()
    const [currentLang, setCurrentLang] = useState(language)
    const [isLanguageOpen, setIsLanguageOpen] = useState(false)
    const [isCurrencyOpen, setIsCurrencyOpen] = useState(false)
    const [currencySearch, setCurrencySearch] = useState('')
    const modalRef = useRef(null)

    const { t } = useTranslation()
    const { updateCurrency: updateCartCurrency } = useCart()

    useEffect(() => {
        const unsubscribe = onLanguageChange(setCurrentLang)
        return () => unsubscribe()
    }, [])

    useEffect(() => {
        const handleClickOutside = e => {
            if (modalRef.current && !modalRef.current.contains(e.target)) {
                onClose()
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [onClose])

    const handleLanguageChange = lang => {
        setLanguage(lang)
        setIsLanguageOpen(false)
    }

    const handleCurrencySelect = async code => {
        const selected = supportedCurrencies.find(c => c.code === code)
        if (!selected) return

        setIsCurrencyOpen(false)
        setCurrencySearch('')

        try {
            // Get exchange rate
            const rate = await fetchExchangeRate(code)

            // Update cart currency on server
            await updateCartCurrency(code)

            // Update local currency preferences
            localStorage.setItem('currency', code)
            localStorage.setItem('currencySymbol', selected.symbol)
            localStorage.setItem('currencyRate', rate.toString())

            window.location.reload()
        } catch (e) {
            console.warn('Failed to update currency', e)
        }
    }

    const filteredCurrencies = supportedCurrencies.filter(c =>
        c.code.toLowerCase().includes(currencySearch.toLowerCase())
    )

    return (
        <div
            ref={modalRef}
            className='fixed inset-0 z-50 flex items-center justify-center bg-black/50'
            onClick={onClose}
        >
            <div
                className='w-11/12 max-w-md mx-auto bg-white rounded-lg shadow-xl'
                onClick={e => e.stopPropagation()}
            >
                <div className='p-6'>
                    <h3 className='mb-4 text-lg font-semibold text-gray-900'>
                        {t('lang.languageCurrency')}
                    </h3>

                    {/* Language Section */}
                    <div className='mb-6'>
                        <h4 className='mb-3 text-sm font-medium text-gray-700'>
                            {t('lang.language')}
                        </h4>
                        <div className='relative'>
                            <button
                                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                                className='flex items-center justify-between w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50'
                            >
                                <div className='flex items-center gap-3'>
                                    <img
                                        src={
                                            currentLang === 'en'
                                                ? '/img/lang/GB.png'
                                                : '/img/lang/SA.png'
                                        }
                                        className='object-cover w-6 h-4 rounded-sm'
                                        alt='Language'
                                    />
                                    <span className='font-medium'>
                                        {currentLang === 'en' ? 'English' : 'العربية'}
                                    </span>
                                </div>
                                <span className='text-gray-400'>▼</span>
                            </button>

                            {isLanguageOpen && (
                                <div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg'>
                                    <button
                                        onClick={() => handleLanguageChange('en')}
                                        className='flex items-center w-full gap-3 px-4 py-3 text-left hover:bg-gray-50'
                                    >
                                        <img
                                            src='/img/lang/GB.png'
                                            className='w-6 h-4'
                                            alt='English'
                                        />
                                        <span>{t('lang.english')}</span>
                                    </button>
                                    <button
                                        onClick={() => handleLanguageChange('ar')}
                                        className='flex items-center w-full gap-3 px-4 py-3 text-left hover:bg-gray-50'
                                    >
                                        <img
                                            src='/img/lang/SA.png'
                                            className='w-6 h-4'
                                            alt='Arabic'
                                        />
                                        <span>{t('lang.arabic')}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Currency Section */}
                    <div>
                        <h4 className='mb-3 text-sm font-medium text-gray-700'>
                            {t('lang.currency')}
                        </h4>
                        <div className='relative'>
                            <button
                                onClick={() => setIsCurrencyOpen(!isCurrencyOpen)}
                                className='flex items-center justify-between w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50'
                            >
                                <div className='font-medium'>
                                    {currency} —{' '}
                                    {supportedCurrencies.find(c => c.code === currency)?.symbol}
                                </div>
                                <span className='text-gray-400'>▼</span>
                            </button>

                            {isCurrencyOpen && (
                                <div className='absolute z-10 w-full mt-1 overflow-hidden bg-white border border-gray-300 rounded-lg shadow-lg max-h-64'>
                                    <div className='p-2 border-b'>
                                        <input
                                            type='text'
                                            value={currencySearch}
                                            onChange={e => setCurrencySearch(e.target.value)}
                                            className='w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-yellow-500'
                                            placeholder='Search currency...'
                                        />
                                    </div>
                                    <div className='overflow-y-auto max-h-48'>
                                        {filteredCurrencies.map(c => (
                                            <button
                                                key={c.code}
                                                onClick={() => handleCurrencySelect(c.code)}
                                                className='flex items-center justify-between w-full px-4 py-3 text-left hover:bg-gray-50'
                                            >
                                                <span>{c.code}</span>
                                                <span className='text-gray-500'>{c.symbol}</span>
                                            </button>
                                        ))}
                                        {filteredCurrencies.length === 0 && (
                                            <div className='px-4 py-3 text-center text-gray-500'>
                                                {t('lang.noResult')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className='flex justify-end px-6 py-4 rounded-b-lg bg-gray-50'>
                    <button
                        onClick={onClose}
                        className='px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50'
                    >
                        {t('lang.done')}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default LanguageCurrencyModal
